import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';
import { STATS, UNIQUE, PORTALS, SPLITS } from './content';

const check = (key) => <SafeIcon key={key} icon={Icons.Check} />;

export default function Home() {
  return (
    <>
      {/* ---------------- hero ---------------- */}
      <section className="mk-hero">
        <div className="mk-container">
          <span className="mk-hero-badge">
            <SafeIcon icon={Icons.Shield} />
            Statutory gas certificates · multi-tenant · passwordless clients
          </span>
          <h1>
            The client portal UK heating &amp; plumbing agencies actually{' '}
            <em>want to use</em>
          </h1>
          <p className="mk-hero-sub">
            Invoices, quotes, job history, documents and live chat for every customer —
            plus a mobile engineer portal with digital job sheets, on-site certificates
            and signatures. One platform, your branding.
          </p>
          <div className="mk-hero-ctas">
            <Link to="/sign-up" className="mk-btn mk-btn-primary">
              Book a demo
              <SafeIcon icon={Icons.ArrowRight} />
            </Link>
            <Link to="/features" className="mk-btn mk-btn-outline">
              Explore features
            </Link>
          </div>
          <p className="mk-hero-note">
            Monthly billing · no setup fee · cancel anytime · your data exports with you
          </p>

          <div className="mk-trust-row">
            <span className="mk-trust-chip"><SafeIcon icon={Icons.Shield} /> Gas Safe certificate engine</span>
            <span className="mk-trust-chip"><SafeIcon icon={Icons.Zap} /> Magic-link client sign-in</span>
            <span className="mk-trust-chip"><SafeIcon icon={Icons.Send} /> Telegram alerts</span>
            <span className="mk-trust-chip"><SafeIcon icon={Icons.CreditCard} /> Stripe payments</span>
            {/* TODO(3.3): restore "Offline-first PWA" chip after offline QA sign-off */}
            <span className="mk-trust-chip"><SafeIcon icon={Icons.Cloud} /> PWA with draft autosave</span>
          </div>
        </div>
      </section>

      {/* ---------------- stat band ---------------- */}
      <section className="mk-stat-band">
        <div className="mk-container mk-stats">
          {STATS.map((s) => (
            <div className="mk-stat" key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- three portals ---------------- */}
      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head">
            <span className="mk-eyebrow">One platform</span>
            <h2>Three portals, one login-free experience for your clients</h2>
            <p>
              The office runs the admin console. Customers get a branded portal they open
              with one click. Engineers run their day from a mobile PWA. Everything stays
              in sync in real time.
            </p>
          </div>
          <div className="mk-grid-3">
            {PORTALS.map((p) => (
              <div className="mk-card" key={p.title}>
                <span className="mk-card-icon"><SafeIcon icon={Icons[p.icon]} /></span>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
                <ul>
                  {p.items.map((item) => (
                    <li key={item}>{check(item)}{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- unique differentiators ---------------- */}
      <section className="mk-section mk-unique">
        <div className="mk-container">
          <div className="mk-section-head">
            <span className="mk-eyebrow">Why we&apos;re different</span>
            <h2>Five things no other trade platform has</h2>
            <p>
              Jobber, Housecall Pro, ServiceM8 and SimPRO are single-tenant, charge
              per-user and can&apos;t produce statutory gas certificates. We built the
              opposite of that.
            </p>
          </div>
          <div className="mk-unique-grid">
            {UNIQUE.map((u) => (
              <div className="mk-unique-item" key={u.title}>
                <SafeIcon icon={Icons[u.icon]} />
                <h3>{u.title}</h3>
                <p>{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- deep dives ---------------- */}
      {SPLITS.map((s, i) => (
        <section className={`mk-section${i % 2 === 1 ? ' mk-alt' : ''}`} key={s.title}>
          <div className="mk-container mk-split">
            <div>
              <span className="mk-eyebrow">{i === 0 ? 'Certificates' : i === 1 ? 'Payments' : 'Notifications'}</span>
              <h2>{s.title}</h2>
              <p>{s.text}</p>
              <ul className="mk-check-list">
                {s.points.map((pt) => (
                  <li key={pt}>{check(pt)}{pt}</li>
                ))}
              </ul>
            </div>
            <div className="mk-panel">
              {s.panel.map((row) => (
                <div className="mk-panel-row" key={row.title}>
                  <SafeIcon icon={Icons[row.icon]} />
                  <div>
                    <strong>{row.title}</strong>
                    <span>{row.sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ---------------- CTA ---------------- */}
      <section className="mk-cta-band">
        <div className="mk-container">
          <h2>Ready to give your clients a portal they&apos;ll love?</h2>
          <p>
            Set up your agency in minutes — branding, first clients and portal links
            included. Cancel anytime.
          </p>
          <div className="mk-hero-ctas">
            <Link to="/sign-up" className="mk-btn mk-btn-lime">
              Book a demo
              <SafeIcon icon={Icons.ArrowRight} />
            </Link>
            <Link to="/pricing" className="mk-btn mk-btn-outline">
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
